import clsx from "clsx";
import styles from "./SelectionDropdown.module.scss";
import { Typography } from "../Typography/Typography";
import { CaretDownIcon } from "@/frontend/shared/assets/icons";
import { HTMLAttributes, ReactNode, useRef } from "react";
import { useOnClickOutside } from "@/frontend/shared/hooks/useOnClickOutside";

type TSelectionOptions = {
  value: string;
  label: string;
};

export type TSelectionDropdownProps = {
  isOpen: boolean;
  options: TSelectionOptions[];
  onChange?: (value: string) => void;
  onClose?: () => void;
  onFocus?: () => void;
  inputSize?: "xs" | "small" | "large";
  value?: string;
  className?: string;
  dropdownPosition?: "top" | "bottom";
  maxHeight?: number;
  isMaxWidth?: boolean;
  children?: ReactNode;
  getValueLabel?: (value: TSelectionOptions | undefined) => string;
  containerClassName?: string;
  optionId?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, "onChange">;

const sizeToHeightMap = {
  xs: "28px",
  small: "40px",
  large: "56px"
};

export function SelectionDropdown({
  options,
  onChange,
  onClose,
  onFocus,
  isOpen,
  value,
  inputSize = "small",
  className,
  dropdownPosition = "bottom",
  maxHeight = 320,
  isMaxWidth,
  children,
  getValueLabel,
  containerClassName,
  optionId,
  ...props
}: TSelectionDropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  function handleSelect(selectedValue: string) {
    onChange?.(selectedValue);
    onClose?.();
  }

  useOnClickOutside(ref, () => {
    onClose?.();
  });

  return (
    <div className={clsx(styles.upperContainer, containerClassName)} ref={ref}>
      <div
        className={clsx(styles.labelContainer, className)}
        onClick={(e) => {
          e.stopPropagation();
          onFocus?.();
        }}
        style={{ height: sizeToHeightMap[inputSize] }}
        {...props}
      >
        {!children && (
          <>
            <Typography variant="body-2" color="greyscale900">
              {getValueLabel ? getValueLabel(options.find((o) => o.value === value)) : value}
            </Typography>
            <CaretDownIcon className={styles.caretDownIcon} />
          </>
        )}
        {children}
      </div>
      <div
        className={clsx(
          styles.container,
          !isOpen && styles.closedContainer,
          dropdownPosition === "top" && styles.topPosition,
          "selection-dropdown-options"
        )}
        style={{ maxHeight, width: isMaxWidth ? "calc(100% - 4px)" : "auto" }}
      >
        {options?.map((option, index) => (
          <Typography
            variant="label-4"
            color="greyscale900"
            key={`${option.label}-${index}`}
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(option.value);
            }}
            className={styles.option}
            id={optionId}
          >
            {option.label}
          </Typography>
        ))}
      </div>
    </div>
  );
}
